import { type Journal, useStores } from '@littlebird/sdk';
import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';
import { useCallback, useEffect, useRef } from 'react';

import {
  getText,
  type JSONContent,
  toDocumentFrom,
} from '@/components/ui/editor';
import { logger } from '@/lib/logger';

import { journalSaves } from './journal-save-state';
import { journalStore } from './journal.store';
import {
  JournalFilterType,
  type JournalOrGap,
  type InsufficientContextEntry,
} from './journal.types';

// Reinterpret a Date's UTC components as a local-timezone date (drops time-of-day)
export const toJournalDisplayDate = (date: Date): Date => {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const createInsufficientContextEntry = (
  startDate: Date,
  endDate: Date
): InsufficientContextEntry => {
  const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
  return {
    type: 'insufficient-context',
    id: `insufficient-context-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}`,
    startDate,
    endDate,
    dayCount,
  };
};

const detectGapsInJournals = (
  journals: Journal[]
): InsufficientContextEntry[] => {
  if (journals.length === 0) return [];

  const gaps: InsufficientContextEntry[] = [];
  const dates = journals
    .map((j) => toJournalDisplayDate(new Date(j.date)))
    .sort((a, b) => a.getTime() - b.getTime());

  for (let i = 0; i < dates.length - 1; i++) {
    const daysDiff = differenceInCalendarDays(dates[i + 1], dates[i]);
    if (daysDiff > 1) {
      gaps.push(
        createInsufficientContextEntry(
          addDays(dates[i], 1),
          addDays(dates[i + 1], -1)
        )
      );
    }
  }

  if (dates.length > 0) {
    const mostRecentDate = dates[dates.length - 1];
    const yesterday = startOfDay(addDays(new Date(), -1));
    if (differenceInCalendarDays(yesterday, mostRecentDate) > 0) {
      gaps.push(
        createInsufficientContextEntry(addDays(mostRecentDate, 1), yesterday)
      );
    }
  }

  return gaps;
};

const createJournalsWithGaps = (journals: Journal[]): JournalOrGap[] => {
  const gaps = detectGapsInJournals(journals);
  const combined: JournalOrGap[] = [...journals, ...gaps];

  const dateMap = new Map<JournalOrGap, Date>();
  for (const item of combined) {
    dateMap.set(
      item,
      'date' in item ? toJournalDisplayDate(new Date(item.date)) : item.endDate
    );
  }

  return combined.sort(
    (a, b) => dateMap.get(b)!.getTime() - dateMap.get(a)!.getTime()
  );
};

const JOURNAL_SAVE_DEBOUNCE_MS = 1_000;

/** One timer per journal id: a save for one journal never cancels another's. Pending saves flush on unmount. */
const useSaveJournal = () => {
  const { journals: store } = useStores();
  const pendingRef = useRef(
    new Map<
      string,
      { timer: ReturnType<typeof setTimeout>; save: () => void }
    >()
  );

  useEffect(() => {
    const pending = pendingRef.current;
    return () => {
      // Flush now and stop the timer, or the same save fires again when the
      // debounce elapses
      for (const { timer, save } of pending.values()) {
        clearTimeout(timer);
        save();
      }
      pending.clear();
    };
  }, []);

  return useCallback(
    (id: string) => {
      const pending = pendingRef.current;
      const previous = pending.get(id);
      if (previous) clearTimeout(previous.timer);

      const activeEntry = journalSaves.schedule(
        id,
        journalSaves.createRetry(id, () => store.saveJournal(id))
      );
      const save = () => {
        pending.delete(id);
        store
          .saveJournal(id)
          .then(() => journalSaves.settle(activeEntry))
          .catch((error: unknown) => {
            logger.error('journal save failed', { error, id });
            journalSaves.fail(activeEntry, error);
          });
      };
      const timer = setTimeout(save, JOURNAL_SAVE_DEBOUNCE_MS);
      pending.set(id, { timer, save });
    },
    [store]
  );
};

export const journalApis = {
  useFilteredJournals: (): JournalOrGap[] => {
    const { journals: journalsStore } = useStores();
    const journals = journalsStore.orderedData;
    const filter = journalStore.journalFilter;

    if (filter === JournalFilterType.UNREAD_ONLY) {
      return journals.filter((journal) => !journal.viewedAt);
    }

    return createJournalsWithGaps(journals);
  },
  useUpdateJournal: () => {
    const { journals: store } = useStores();
    const save = useSaveJournal();

    return (
      id: string,
      data: Partial<{
        document: JSONContent;
        title: string;
      }>
    ) => {
      store.updateJournal(id, data, getText);
      save(id);
    };
  },
  useRestoreJournal: () => {
    const { journals: store } = useStores();

    return (id: string) => {
      store.restoreJournal(id, toDocumentFrom);
      store.saveJournal(id);
    };
  },
};
